import { TableHeaderPosition, type ITableSettings } from "../types";
import { calcCellText } from "./calc-cell-text";
import { extractCellText } from "./extract-cell-text";
import { parseSortableValue, type SortableKind } from "./parse-sortable-value";
import { makeCellResolver } from "./resolve-cell-text";

export const buildRowGroups = (
	tableData: ITableSettings["tableData"],
	tableCols: number,
	tableRows: number,
): number[][] => {
	const parent = Array.from({ length: tableRows }, (_, i) => i);
	const find = (x: number): number => {
		while (parent[x] !== x) {
			parent[x] = parent[parent[x]];
			x = parent[x];
		}
		return x;
	};
	const union = (a: number, b: number) => {
		const ra = find(a);
		const rb = find(b);
		if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb);
	};

	for (let i = 0; i < tableData.length; i++) {
		const cell = tableData[i];
		if (cell.mergedInto) continue;
		const rowSpan = cell.rowSpan ?? 1;
		if (rowSpan > 1) {
			const r0 = Math.floor(i / tableCols);
			const rEnd = Math.min(r0 + rowSpan, tableRows);
			for (let r = r0 + 1; r < rEnd; r++) union(r0, r);
		}
	}

	const byRep = new Map<number, number[]>();
	for (let r = 0; r < tableRows; r++) {
		const rep = find(r);
		const arr = byRep.get(rep);
		if (arr) arr.push(r);
		else byRep.set(rep, [r]);
	}
	return Array.from(byRep.values()).sort((a, b) => a[0] - b[0]);
};

export const sortRowOrder = (
	settings: Pick<
		ITableSettings,
		| "tableData"
		| "tableCols"
		| "tableRows"
		| "tableHasHeader"
		| "tableHeaderPosition"
	>,
	col: number,
	direction: "asc" | "desc",
): number[] | null => {
	const {
		tableCols,
		tableRows,
		tableData,
		tableHasHeader,
		tableHeaderPosition,
	} = settings;
	if (!tableData || !tableCols || !tableRows) return null;
	if (col < 0 || col >= tableCols) return null;

	const allGroups = buildRowGroups(tableData, tableCols, tableRows);

	// Only the header row is pinned at its position; every data row (including
	// calc/formula rows) sorts by its value.
	const headerRow =
		tableHasHeader && tableHeaderPosition === TableHeaderPosition.Top
			? 0
			: tableHasHeader && tableHeaderPosition === TableHeaderPosition.Bottom
				? tableRows - 1
				: null;
	const isPinned = (group: number[]): boolean =>
		headerRow !== null && group.includes(headerRow);

	const sortableGroups = allGroups.filter((g) => !isPinned(g));

	const { resolveCell } = makeCellResolver(tableData);
	const getGroupSortKey = (group: number[]) => {
		let text = "";
		for (const r of group) {
			const anchor = resolveCell(tableData[col + r * tableCols]);
			if (!anchor) continue;
			const t =
				calcCellText(anchor, tableData, tableCols, tableRows, col, r) ??
				extractCellText(anchor.content);
			if (!t) continue;
			if ((anchor.colSpan ?? 1) === 1) {
				text = t;
				break;
			}
			if (!text) text = t;
		}
		return { text, parsed: parseSortableValue(text) };
	};

	const keyed = sortableGroups.map((g) => ({
		group: g,
		...getGroupSortKey(g),
	}));
	// Empty cells and calc errors (#REF/#ERROR) sort to the end and don't count
	// toward the column kind, so a numeric column with a formula error still
	// sorts its numbers numerically instead of falling back to a text sort.
	const isSortEmpty = (text: string): boolean =>
		text.length === 0 || /^#(REF|ERROR)$/.test(text);
	const [withContent, empty] = keyed.reduce(
		(acc, x) => {
			acc[isSortEmpty(x.text) ? 1 : 0].push(x);
			return acc;
		},
		[[], []] as [typeof keyed, typeof keyed],
	);

	const kindOf = (k: SortableKind) =>
		withContent.every((x) => x.parsed.kind === k);
	const columnKind: SortableKind =
		withContent.length === 0
			? "text"
			: kindOf("number")
				? "number"
				: kindOf("date")
					? "date"
					: "text";

	withContent.sort((a, b) => {
		if (columnKind === "number" || columnKind === "date") {
			return (a.parsed.value as number) - (b.parsed.value as number);
		}
		return a.text.localeCompare(b.text);
	});
	if (direction === "desc") withContent.reverse();

	const sortedSortable: number[][] = [
		...withContent.map((x) => x.group),
		...empty.map((x) => x.group),
	];

	// Walk the original group order; the pinned header keeps its slot, every
	// other slot (including calc/formula rows) is filled by the next sorted group.
	let s = 0;
	const finalGroups = allGroups.map((g) => (isPinned(g) ? g : sortedSortable[s++]));

	const order: number[] = [];
	for (const group of finalGroups) order.push(...group);
	return order;
};
